/**
 * What X charges us, in credits (1 credit = $0.001 USD), and what a monthly
 * credit budget buys in posts.
 *
 * Kept free of database imports so the web app can quote the allowance from
 * the same numbers the meter debits (`x-credits.ts` re-exports the rates).
 * Rates mirror X's published pay-per-use pricing. Keep them in sync.
 * @see https://docs.x.com/x-api — pay-per-use rate card
 */

/** 1 credit = $0.001 USD. Integer credits keep the counter exact. */
export const X_CREDIT_USD = 0.001;

/** $0.015 — a post created with no link in it. */
export const X_CREDITS_TWEET = 15;
/** $0.20 — a post created containing a URL. 13x the plain rate. */
export const X_CREDITS_TWEET_WITH_URL = 200;
/** $0.005 — one post read (`GET /2/tweets/:id`, the metrics sync). */
export const X_CREDITS_POST_READ = 5;
/** $0.010 — one user read (`GET /2/users/:id`, account metrics). */
export const X_CREDITS_USER_READ = 10;

/**
 * Metric reads one X post gets over its life: every 12 hours for two days,
 * daily to a week, then every three days until the X window closes (the
 * age-decayed cadence in `analytics/metrics-sync.ts`).
 */
export const X_METRIC_READS_PER_POST = 11;

/** A plain post's lifetime cost: the create plus its metric reads. */
export const X_CREDITS_PER_PLAIN_POST =
  X_CREDITS_TWEET + X_METRIC_READS_PER_POST * X_CREDITS_POST_READ;

/** A post with a link: the 13x create plus the same reads. */
export const X_CREDITS_PER_LINK_POST =
  X_CREDITS_TWEET_WITH_URL + X_METRIC_READS_PER_POST * X_CREDITS_POST_READ;

/**
 * Roughly how many single posts a monthly budget covers, rounded DOWN to a
 * figure worth quoting (5s under 100, 10s above) so a promise made from it
 * always holds. A thread costs one post per segment.
 */
export function xPostsPerMonth(credits: number) {
  return {
    plain: roundDownForQuote(Math.floor(credits / X_CREDITS_PER_PLAIN_POST)),
    withLink: roundDownForQuote(Math.floor(credits / X_CREDITS_PER_LINK_POST)),
  };
}

function roundDownForQuote(posts: number): number {
  const step = posts < 100 ? 5 : 10;

  return Math.floor(posts / step) * step;
}
